import React from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useNews } from '../context/NewsContext';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import NewsBannerImage from '../assets/news.png';
import pgLogo from '../assets/pglogo.png';
import './News.css';

const ITEMS_PER_PAGE = 6;

// An update / event is flagged "New" for this many days, counting the day it
// was posted as day 1 (so day 1 to day 7 = posting day + the next 6 days).
const NEW_BADGE_DAYS = 7;

// The modal is rendered in a portal on document.body, so it no longer sits
// inside .news-page where the brand CSS variables are defined. Pass them
// inline so the modal styling keeps working. zIndex keeps it above the navbar.
const MODAL_OVERLAY_STYLE = {
  '--pg-dark': '#005145',
  '--pg-green': '#006a53',
  '--pg-pink': '#f192b0',
  '--pg-yellow': '#dedc00',
  '--pg-orange': '#f39200',
  '--pg-font': "'New Hero', 'Poppins', system-ui, -apple-system, 'Segoe UI', sans-serif",
  zIndex: 9999,
};

// Query param set by notification links: /whats-happening?item=<mondayItemId>
const ITEM_PARAM = 'item';

// --- Monday: Links/Extras board -------------------------------------------
// Board "Links/Extras" (Intranet CMS workspace, id 5102367564) holds simple
// key/value links so they can be edited in Monday without a redeploy.
//
// NOTE: no GraphQL fragments here on purpose. `... on LinkValue { url }` only
// exists from API-Version 2023-10 onward, and on an older version the whole
// query errors out and data comes back null. Reading the link column's raw
// `value` JSON works on every version.
const MONDAY_API_URL = 'https://api.monday.com/v2';
const MONDAY_TOKEN =
  process.env.REACT_APP_MONDAY_API_KEY ||
  process.env.REACT_APP_MONDAY_TOKEN ||
  process.env.REACT_APP_MONDAY_API_TOKEN ||
  '';
const LINKS_BOARD_ID = process.env.REACT_APP_MONDAY_LINKS_BOARD_ID;
// Optional hardcoded escape hatch — set this in .env to bypass Monday entirely.
const STATIC_CALENDAR_URL = process.env.REACT_APP_EVENTS_CALENDAR_URL || '';
const EVENTS_CALENDAR_ITEM_NAME = 'events calendar link';

const LINKS_QUERY = `
  query ($boardId: [ID!]) {
    boards(ids: $boardId) {
      id
      name
      items_page(limit: 100) {
        items {
          id
          name
          column_values {
            id
            type
            text
            value
          }
        }
      }
    }
  }
`;

const URL_RE = /(https?:\/\/[^\s"',<>]+)/i;

// A Monday link column stores {"url":"https://...","text":"label"} in `value`;
// its `text` is usually "https://... - label". Try both.
const extractUrl = (col) => {
  if (!col) return null;

  if (col.value) {
    try {
      const parsed = JSON.parse(col.value);
      if (parsed && typeof parsed.url === 'string' && URL_RE.test(parsed.url)) {
        return parsed.url.trim();
      }
    } catch (e) {
      // value wasn't JSON — fall through to the text check
    }
  }

  const match = URL_RE.exec(col.text || '');
  return match ? match[1] : null;
};

const fetchEventsCalendarLink = async () => {
  if (STATIC_CALENDAR_URL) return STATIC_CALENDAR_URL;

  if (!LINKS_BOARD_ID) {
    console.warn('[Links board] REACT_APP_MONDAY_LINKS_BOARD_ID is not set (restart the dev server after editing .env).');
    return null;
  }
  if (!MONDAY_TOKEN) {
    console.warn('[Links board] No Monday API token found. Check which REACT_APP_* var NewsBox.js uses and add it to the MONDAY_TOKEN chain in News.js.');
    return null;
  }

  const res = await fetch(MONDAY_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: MONDAY_TOKEN,
      'API-Version': '2023-10',
    },
    body: JSON.stringify({
      query: LINKS_QUERY,
      variables: { boardId: [String(LINKS_BOARD_ID)] },
    }),
  });

  if (!res.ok) {
    console.warn('[Links board] HTTP', res.status, await res.text());
    return null;
  }

  const json = await res.json();

  if (json.errors || json.error_message) {
    console.warn('[Links board] GraphQL error:', json.errors || json.error_message);
    return null;
  }

  const board = json?.data?.boards?.[0];
  if (!board) {
    console.warn('[Links board] Board not found or the token has no access to board', LINKS_BOARD_ID);
    return null;
  }

  const items = board?.items_page?.items || [];
  const item =
    items.find((i) => (i.name || '').trim().toLowerCase() === EVENTS_CALENDAR_ITEM_NAME) ||
    items.find((i) => (i.name || '').toLowerCase().includes('calendar'));

  if (!item) {
    console.warn('[Links board] No item named "Events Calendar Link". Items found:', items.map((i) => i.name));
    return null;
  }

  const cols = item.column_values || [];
  const linkCol = cols.find((c) => c.type === 'link' && extractUrl(c));
  const anyCol = cols.find((c) => extractUrl(c));
  const url = extractUrl(linkCol || anyCol);

  if (!url) {
    console.warn('[Links board] Item found but no URL in any column:', cols);
    return null;
  }

  return url;
};
// ---------------------------------------------------------------------------

// --- Field mapping ---------------------------------------------------------
// fetchNews() (NewsBox.js) returns: id, title, date (Publishing Date),
// eventDate, hour, location, body (News text), eventText (Event text),
// type ('news'|'event'), image, images.
const getItemType = (item) => item.type || 'news';
const getItemBody = (item) => (item.type === 'event' ? item.eventText : item.body) || '';
const getItemImage = (item) => item.image || null;
const getItemTitle = (item) => item.title || '';

// Label used for the "Showing X of Y ..." line and the empty state.
// Follows the active filter: Events filter -> events, everything else -> updates.
const getCountNoun = (filter, count) => {
  if (filter === 'event') return count === 1 ? 'event' : 'events';
  return count === 1 ? 'update' : 'updates';
};

// Publishing Date shown as DD/MM/YYYY. Handles a plain "YYYY-MM-DD" string
// directly (no timezone conversion involved) and falls back to Date parsing
// for anything else Monday might hand back.
const formatDateDMY = (raw) => {
  if (!raw) return '';
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
};

// The date shown on the card: events show their Event Date, news items show
// their Publishing Date — both formatted DD/MM/YYYY.
const getItemDisplayDateText = (item) =>
  formatDateDMY(item.type === 'event' ? item.eventDate || item.date : item.date);

const toDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

// Date used for filtering the feed itself (Publishing Date governs
// when an item appears in the news list).
const parseItemDate = (item) => toDate(item.date);

// Date used specifically for calendar placement — events use their Event
// Date, falling back to Publishing Date if Event Date isn't set. This is the
// date that actually gets marked on the calendar, never Publishing Date.
const parseCalendarDate = (item) =>
  item.type === 'event' ? toDate(item.eventDate) || toDate(item.date) : null;

// "New" is driven by the Publishing Date (when it was posted), for both
// updates and events. A plain "YYYY-MM-DD" is read as a LOCAL calendar day so
// the badge flips at local midnight, not at UTC midnight.
const toLocalDay = (raw) => {
  if (!raw) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isNewItem = (item) => {
  const posted = toLocalDay(item.date);
  if (!posted) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysSincePosted = Math.round((today - posted) / 86400000);
  return daysSincePosted >= 0 && daysSincePosted < NEW_BADGE_DAYS;
};

// Mirrors the Monday board sort: Publishing Date (newest first), then
// Event Date (newest first). Blank dates sort to the top, like Monday does
// when sorting descending. Array.sort is stable, so anything still tied
// keeps the order it came back from Monday.
const dateValue = (raw) => {
  const d = toDate(raw);
  return d ? d.getTime() : null;
};

const compareDesc = (a, b) => {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return b - a;
};

const compareNewestFirst = (a, b) =>
  compareDesc(dateValue(a.date), dateValue(b.date)) ||
  compareDesc(dateValue(a.eventDate), dateValue(b.eventDate));
// ---------------------------------------------------------------------------

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Small "opens in a new tab" arrow, drawn as SVG so no unicode/entity glyphs
// end up in JSX text nodes.
const ExternalArrow = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17L17 7" />
    <path d="M9 7h8v8" />
  </svg>
);

// "New" marker: a small pill with a softly pulsing dot. Used on cards (pinned
// to the top-right of the image) and inline inside the modal.
const NewTag = ({ inline = false }) => (
  <span className={`news-new-tag ${inline ? 'news-new-tag--inline' : ''}`.trim()}>
    <span className="news-new-tag-dot" aria-hidden="true" />
    New
  </span>
);

const buildMonthMatrix = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const NewsCalendar = ({ events, selectedDate, onSelectDate }) => {
  const [visibleMonth, setVisibleMonth] = React.useState(() => {
    const today = new Date();
    const nextUpcoming = events
      .filter((d) => d >= new Date(today.getFullYear(), today.getMonth(), today.getDate()))
      .sort((a, b) => a - b)[0];
    const base = nextUpcoming || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const today = new Date();
  const cells = buildMonthMatrix(visibleMonth);

  const eventsOnDay = (day) => (day ? events.filter((e) => sameDay(e, day)) : []);

  const goToMonth = (delta) => {
    setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const upcoming = events
    .filter((d) => d >= new Date(today.getFullYear(), today.getMonth(), today.getDate()))
    .sort((a, b) => a - b)
    .slice(0, 4);

  return (
    <aside className="news-calendar-card">
      <div className="news-calendar-header">
        <button className="news-calendar-nav" onClick={() => goToMonth(-1)} aria-label="Previous month">
          &#8249;
        </button>
        <p className="news-calendar-month">
          {MONTH_NAMES[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}
        </p>
        <button className="news-calendar-nav" onClick={() => goToMonth(1)} aria-label="Next month">
          &#8250;
        </button>
      </div>

      <div className="news-calendar-weekdays">
        {WEEKDAY_LABELS.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>

      <div className="news-calendar-grid">
        {cells.map((day, i) => {
          if (!day) return <span key={i} className="news-calendar-cell empty" />;
          const dayEvents = eventsOnDay(day);
          const hasEvent = dayEvents.length > 0;
          const isToday = sameDay(day, today);
          const isSelected = selectedDate && sameDay(day, selectedDate);
          return (
            <button
              key={i}
              className={[
                'news-calendar-cell',
                hasEvent ? 'has-event' : '',
                isToday ? 'is-today' : '',
                isSelected ? 'is-selected' : '',
              ]
                .join(' ')
                .trim()}
              onClick={() => onSelectDate(hasEvent ? (isSelected ? null : day) : null)}
              disabled={!hasEvent}
            >
              <span className="news-calendar-cell-num">{day.getDate()}</span>
              {hasEvent && <span className="news-calendar-dot" />}
            </button>
          );
        })}
      </div>

      <div className="news-calendar-legend">
        <span className="news-calendar-legend-item">
          <span className="news-calendar-legend-swatch today" />
          Today
        </span>
        <span className="news-calendar-legend-item">
          <span className="news-calendar-legend-swatch event" />
          Event
        </span>
      </div>

      <div className="news-calendar-upcoming">
        <p className="news-calendar-upcoming-label">Upcoming events</p>
        {upcoming.length === 0 && <p className="news-calendar-empty">No upcoming events</p>}
        {upcoming.map((d, i) => (
          <button
            key={i}
            className="news-calendar-upcoming-item"
            onClick={() => onSelectDate(sameDay(d, selectedDate) ? null : d)}
          >
            <span className="news-calendar-upcoming-date">
              {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
            <span className="news-calendar-upcoming-dot" />
          </button>
        ))}
      </div>
    </aside>
  );
};

const News = () => {
  const { news, loading, error, clearUnread } = useNews();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [calendarLink, setCalendarLink] = React.useState(null);

  React.useEffect(() => {
    clearUnread();
  }, [clearUnread]);

  React.useEffect(() => {
    let cancelled = false;
    fetchEventsCalendarLink()
      .then((url) => {
        if (!cancelled) setCalendarLink(url);
      })
      .catch((err) => {
        console.warn('[Links board] Request failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the modal and drop ?item= from the URL, so clicking the same
  // notification again reopens it.
  const closeModal = React.useCallback(() => {
    setSelectedItem(null);
    const params = new URLSearchParams(location.search);
    if (params.has(ITEM_PARAM)) {
      params.delete(ITEM_PARAM);
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  // Notification deep link: /whats-happening?item=<id> opens that item's modal
  // once the news has loaded. Runs again if the param changes while the page
  // is already open.
  React.useEffect(() => {
    const itemId = new URLSearchParams(location.search).get(ITEM_PARAM);
    if (!itemId || loading || !news.length) return;

    const match = news.find((i) => String(i.id) === String(itemId));
    if (match) setSelectedItem(match);
  }, [location.search, news, loading]);

  // Anchor the modal: lock page scroll while it's open (compensating for the
  // scrollbar so the layout doesn't jump).
  React.useEffect(() => {
    if (!selectedItem) return undefined;

    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [selectedItem]);

  // Escape closes the modal.
  React.useEffect(() => {
    if (!selectedItem) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedItem, closeModal]);

  // Same order as the Monday board: Publishing Date, then Event Date.
  const sortedNews = React.useMemo(() => [...news].sort(compareNewestFirst), [news]);

  const filteredNews = React.useMemo(() => {
    let list = sortedNews;
    if (activeFilter !== 'all') {
      list = list.filter((item) => getItemType(item) === activeFilter);
    }
    if (selectedDate) {
      // When a calendar date is selected, match against the same date field
      // used to place items on the calendar (Event Date for events).
      list = list.filter((item) => {
        const d = getItemType(item) === 'event' ? parseCalendarDate(item) : parseItemDate(item);
        return sameDay(d, selectedDate);
      });
    }
    return list;
  }, [sortedNews, activeFilter, selectedDate]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, selectedDate]);

  React.useEffect(() => {
    const total = Math.max(1, Math.ceil(filteredNews.length / ITEMS_PER_PAGE));
    if (currentPage > total) setCurrentPage(1);
  }, [filteredNews, currentPage]);

  // Only events, placed on the calendar using their Event Date (never
  // Publishing Date), falling back to Publishing Date only if Event Date
  // isn't set on that particular item.
  const eventDates = React.useMemo(
    () =>
      news
        .filter((item) => getItemType(item) === 'event')
        .map(parseCalendarDate)
        .filter(Boolean),
    [news]
  );

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentItems = filteredNews.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    if (currentPage <= 3) return [1, 2, 3, 4, '...', totalPages];
    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const modal = (
    <AnimatePresence>
      {selectedItem && (
        <motion.div
          className="news-modal-overlay"
          style={MODAL_OVERLAY_STYLE}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeModal}
        >
          <motion.div
            className="news-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label={getItemTitle(selectedItem)}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="news-modal-close" onClick={closeModal} aria-label="Close">
              &#10005;
            </button>

            <div className="news-modal-media">
              {getItemImage(selectedItem) ? (
                <img
                  src={getItemImage(selectedItem)}
                  alt={getItemTitle(selectedItem)}
                  className="news-modal-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = pgLogo;
                    e.currentTarget.classList.add('news-modal-img--fallback-src');
                  }}
                />
              ) : (
                <div className={`news-modal-img-fallback ${getItemType(selectedItem)}`}>
                  <img
                    src={pgLogo}
                    alt="Project Green"
                    className="news-modal-img-fallback-logo"
                  />
                </div>
              )}
              <span className={`news-card-badge ${getItemType(selectedItem)}`}>
                {getItemType(selectedItem) === 'event' ? 'Event' : 'Update'}
              </span>
            </div>

            <div className="news-modal-body-wrap">
              {(getItemDisplayDateText(selectedItem) || isNewItem(selectedItem)) && (
                <div className="news-modal-date-row">
                  {getItemDisplayDateText(selectedItem) && (
                    <span className="news-card-date">{getItemDisplayDateText(selectedItem)}</span>
                  )}
                  {isNewItem(selectedItem) && <NewTag inline />}
                </div>
              )}
              <h2 className="news-modal-title">{getItemTitle(selectedItem)}</h2>

              {getItemType(selectedItem) === 'event' && (selectedItem.hour || selectedItem.location) && (
                <div className="news-modal-meta">
                  {selectedItem.hour && (
                    <span className="news-modal-meta-item">
                      <svg className="news-card-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                      <span className="news-modal-meta-text">{selectedItem.hour}</span>
                    </span>
                  )}
                  {selectedItem.location && (
                    <span className="news-modal-meta-item">
                      <svg className="news-card-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                        <circle cx="12" cy="9.5" r="2.4" />
                      </svg>
                      <span className="news-modal-meta-text">{selectedItem.location}</span>
                    </span>
                  )}
                </div>
              )}

              <p className="news-modal-body">{getItemBody(selectedItem)}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="news-page">
      <NavBar />

      <section className="news-banner" style={{ backgroundImage: `url(${NewsBannerImage})` }}>
        <div className="news-banner-overlay" />
        <div className="news-banner-text">
          <h1 className="news-banner-heading">What's Happening</h1>
          <p className="news-banner-sub">Latest Update and Events</p>
        </div>
      </section>

      <main className="news-page-content">
        <div className="news-page-watermark" aria-hidden="true">
          <span>UPDATES &amp; EVENTS</span>
        </div>

        {calendarLink ? (
          <div className="news-hero-cta-row">
            <a className="news-hero-cta" href={calendarLink} target="_blank" rel="noopener noreferrer">
              <span className="news-hero-cta-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="5" width="18" height="16" rx="3" />
                  <path d="M3 10h18" />
                  <path d="M8 3v4" />
                  <path d="M16 3v4" />
                </svg>
              </span>
              <span className="news-hero-cta-text">
                <span className="news-hero-cta-kicker">Events Calendar</span>
                <span className="news-hero-cta-title">Want to know more?</span>
              </span>
              <ExternalArrow className="news-hero-cta-arrow" />
            </a>
          </div>
        ) : null}

        <div className="news-filter-row">
          <div className="news-filter-group">
            {['all', 'news', 'event'].map((f) => (
              <button
                key={f}
                className={`news-filter-btn ${f} ${activeFilter === f ? 'active' : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'news' ? 'Updates' : 'Events'}
              </button>
            ))}
          </div>

          {selectedDate ? (
            <button className="news-filter-clear" onClick={() => setSelectedDate(null)}>
              {selectedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} &#10005;
            </button>
          ) : null}
        </div>

        <div className="news-layout">
          <div className="news-feed-shell">
            {loading && <p className="news-page-status">Loading...</p>}
            {error && <p className="news-page-status">Failed to load updates.</p>}
            {!loading && !error && filteredNews.length === 0 && (
              <p className="news-page-status">
                No {activeFilter === 'event' ? 'events' : 'updates'} found.
              </p>
            )}

            {!loading && !error && filteredNews.length > 0 && (
              <>
                <div className="news-feed-topbar">
                  <p className="news-feed-count">
                    Showing <strong>{currentItems.length}</strong> of {filteredNews.length}{' '}
                    {getCountNoun(activeFilter, filteredNews.length)}
                  </p>
                  <p className="news-feed-page-label">
                    Page {currentPage} of {totalPages}
                  </p>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentPage + activeFilter + (selectedDate ? selectedDate.toISOString() : '')}
                    className="news-feed-grid"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    {currentItems.map((item, index) => {
                      const type = getItemType(item);
                      const image = getItemImage(item);
                      const body = getItemBody(item);
                      const title = getItemTitle(item);
                      const dateText = getItemDisplayDateText(item);
                      const isNew = isNewItem(item);
                      return (
                        <motion.article
                          className={`news-card ${isNew ? 'is-new' : ''}`.trim()}
                          key={item.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.04 }}
                          onClick={() => setSelectedItem(item)}
                        >
                          <div className="news-card-media">
                            {image ? (
                              <img
                                src={image}
                                alt={title}
                                className="news-card-img"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = pgLogo;
                                  e.currentTarget.classList.add('news-card-img--fallback-src');
                                }}
                              />
                            ) : (
                              <div className={`news-card-img-fallback ${type}`}>
                                <img
                                  src={pgLogo}
                                  alt="Project Green"
                                  className="news-card-img-fallback-logo"
                                />
                              </div>
                            )}
                            <span className={`news-card-badge ${type}`}>
                              {type === 'event' ? 'Event' : 'Update'}
                            </span>
                            {isNew && <NewTag />}
                          </div>

                          <div className="news-card-body-wrap">
                            {dateText && <span className="news-card-date">{dateText}</span>}
                            <h2 className="news-card-title">{title}</h2>
                            <p className="news-card-body">{body}</p>

                            {type === 'event' && (item.hour || item.location) && (
                              <div className="news-card-meta">
                                {item.hour && (
                                  <span className="news-card-meta-item">
                                    <svg className="news-card-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="9" />
                                      <path d="M12 7v5l3 3" />
                                    </svg>
                                    <span className="news-card-meta-text">{item.hour}</span>
                                  </span>
                                )}
                                {item.location && (
                                  <span className="news-card-meta-item">
                                    <svg className="news-card-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                                      <circle cx="12" cy="9.5" r="2.4" />
                                    </svg>
                                    <span className="news-card-meta-text">{item.location}</span>
                                  </span>
                                )}
                              </div>
                            )}

                            <span className="news-card-cta">Read more &#8594;</span>
                          </div>
                        </motion.article>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>

                {totalPages > 1 && (
                  <div className="news-pagination-wrap">
                    <nav className="news-pagination" aria-label="Update pages">
                      <button
                        className="news-page-btn news-page-nav"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        &#8592; Prev
                      </button>
                      {getVisiblePages().map((page, index) =>
                        page === '...' ? (
                          <span key={`dots-${index}`} className="news-page-dots">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`news-page-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                            aria-current={currentPage === page ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        className="news-page-btn news-page-nav"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next &#8594;
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>

          <NewsCalendar events={eventDates} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
        </div>
      </main>

      {/* Portal to <body> so the modal is anchored to the viewport, independent
          of any ancestor overflow/transform/stacking context. */}
      {ReactDOM.createPortal(modal, document.body)}

      <Footer />
    </div>
  );
};

export default News;